/* Every formatter here produces MONO-destined text: timestamps, durations,
   SHAs, counts, byte sizes. Language belongs in Schibsted Grotesk; data belongs here.

   Duplicated from admin-dashboard/src/lib/format.ts, with the additions this
   surface needs (lease countdowns and file paths). */

const dtf = new Intl.DateTimeFormat(undefined, {
  year: 'numeric',
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

const timeOnly = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});

export function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return dtf.format(d);
}

export function formatClock(value?: string | null): string {
  if (!value) return '--:--:--';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '--:--:--';
  return timeOnly.format(d);
}

/** Compact relative age, e.g. `4m`, `3h`, `2d`. Empty input reads `—`. */
export function formatAge(value?: string | null, now: number = Date.now()): string {
  if (!value) return '—';
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return '—';
  const secs = Math.max(0, Math.round((now - t) / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m`;
  const hours = Math.round(mins / 60);
  if (hours < 48) return `${hours}h`;
  return `${Math.round(hours / 24)}d`;
}

/**
 * Time remaining, e.g. `12m left`, `expired`.
 *
 * Used for the claim lease. The lease is the reason a chat an expert walked
 * away from returns to the queue instead of stranding a frozen project, so the
 * number is worth showing rather than hiding behind a refresh.
 */
export function formatRemaining(value?: string | null, now: number = Date.now()): string {
  if (!value) return '—';
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return '—';
  const secs = Math.round((t - now) / 1000);
  if (secs <= 0) return 'expired';
  if (secs < 60) return `${secs}s left`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m left`;
  const hours = Math.floor(mins / 60);
  return `${hours}h ${String(mins % 60).padStart(2, '0')}m left`;
}

/** Elapsed between two instants as `m:ss` or `h:mm:ss`. Used on ledger stops. */
export function formatDuration(fromISO?: string | null, toISO?: string | null, now: number = Date.now()): string {
  if (!fromISO) return '—';
  const from = new Date(fromISO).getTime();
  if (Number.isNaN(from)) return '—';
  const to = toISO ? new Date(toISO).getTime() : now;
  return formatMillis(Math.max(0, to - from));
}

export function formatMillis(ms?: number | null): string {
  if (ms == null || Number.isNaN(ms)) return '—';
  const total = Math.round(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Seven characters is what a reviewer actually reads off a SHA. */
export function shortSha(sha?: string | null): string {
  if (!sha) return '—';
  return sha.slice(0, 7);
}

export function pluralize(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** A signed count, for diff totals: `+128 −14`. The minus is U+2212, not a
    hyphen, so the two columns align in a mono face. */
export function formatDelta(additions?: number, deletions?: number): string {
  const a = additions ?? 0;
  const d = deletions ?? 0;
  return `+${a} −${d}`;
}

export function formatBytes(bytes?: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** The last path segment, for a file-tree leaf. */
export function basename(path: string): string {
  const i = path.lastIndexOf('/');
  return i === -1 ? path : path.slice(i + 1);
}

/** Everything before the last segment, with the trailing slash dropped. */
export function dirname(path: string): string {
  const i = path.lastIndexOf('/');
  return i === -1 ? '' : path.slice(0, i);
}

/** Middle-elides a long path so both ends stay readable in a fixed column. */
export function elidePath(path: string, max = 64): string {
  if (path.length <= max) return path;
  const head = Math.ceil((max - 1) / 2);
  const tail = Math.floor((max - 1) / 2);
  return `${path.slice(0, head)}…${path.slice(path.length - tail)}`;
}
